const ANTENNA_TIME_ZONE = 'Europe/Paris'

/**
 * Journée d'antenne, côté interface.
 *
 * Jumeau de `backend/src/lib/broadcast-day.ts` : mêmes bornes, mêmes règles.
 * L'agenda dessine une journée de diffusion, pas la journée du poste de
 * l'opérateur. Tant que la grille se calculait en heure du navigateur, un poste
 * mal réglé — ou simplement en déplacement — décalait toute la timeline sans
 * rien signaler ; c'est déjà la raison d'être de `paris-date-time.ts` pour les
 * champs de saisie.
 *
 * Deux règles à retenir :
 *
 * - la journée est **demi-ouverte** `[minuit, minuit du lendemain[`. Un élément
 *   qui finit pile à minuit appartient au jour qui l'a diffusé, un élément qui
 *   commence pile à minuit au jour suivant, jamais aux deux. La borne
 *   « 23:59:59.999 » qu'on écrivait avant laissait la dernière milliseconde de
 *   la nuit hors de toute journée ;
 *
 * - la position se mesure en **temps écoulé depuis minuit d'antenne**, pas en
 *   `getHours()`. Les deux dimanches de changement d'heure durent 23 h et 25 h :
 *   en heure d'horloge, le 25 octobre voit 02:00 revenir une seconde fois, et
 *   deux programmes différents se superposaient au même pixel.
 */

const PARTS_FORMATTER = new Intl.DateTimeFormat('fr-CA', {
  timeZone:  ANTENNA_TIME_ZONE,
  hourCycle: 'h23',
  year:      'numeric',
  month:     '2-digit',
  day:       '2-digit',
  hour:      '2-digit',
  minute:    '2-digit',
  second:    '2-digit',
})

export function antennaParts(timestampMs: number): Record<string, number> {
  return Object.fromEntries(
    PARTS_FORMATTER
      .formatToParts(new Date(timestampMs))
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, Number(part.value)]),
  )
}

/** Décalage du fuseau d'antenne à un instant donné, en millisecondes. */
export function antennaOffsetMs(timestampMs: number): number {
  const parts = antennaParts(timestampMs)
  const representedAsUtc = Date.UTC(
    parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second,
  )
  return representedAsUtc - Math.floor(timestampMs / 1000) * 1000
}

/** Instant réel d'une heure d'horloge d'antenne. */
function instantOf(year: number, month: number, day: number, hour = 0, minute = 0, ms = 0): Date {
  const wallClock = Date.UTC(year, month - 1, day, hour, minute, 0, ms)
  // Deux passes : le décalage à appliquer est celui qui règne à l'instant
  // cherché, pas celui de minuit UTC — sinon les lendemains de changement
  // d'heure démarrent une heure trop tôt ou trop tard.
  const firstGuess = wallClock - antennaOffsetMs(wallClock)
  return new Date(wallClock - antennaOffsetMs(firstGuess))
}

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * Jour civil d'antenne d'une valeur. Seule une clé nue `AAAA-MM-JJ` se lit
 * telle quelle ; une chaîne horodatée est un **instant** et se rapporte au
 * fuseau d'antenne. Couper ses dix premiers caractères lisait la date UTC :
 * un trou à 00:40 le 25 tombait le 24, et le comblage était rangé la veille.
 */
function civilDayOf(value: string | Date): [number, number, number] {
  if (typeof value === 'string') {
    const instant = DAY_KEY_PATTERN.test(value) ? Number.NaN : Date.parse(value)
    if (Number.isNaN(instant)) {
      const [year, month, day] = value.slice(0, 10).split('-').map(Number)
      return [year, month, day]
    }
    value = new Date(instant)
  }
  const parts = antennaParts(value.getTime())
  return [parts.year, parts.month, parts.day]
}

/** Jour civil d'antenne au format `AAAA-MM-JJ`. */
export function broadcastDayKey(value: string | Date): string {
  const [year, month, day] = civilDayOf(value)
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Aujourd'hui, à l'antenne — qui n'est pas toujours aujourd'hui au poste.
 *
 * `now` est **obligatoire** et doit venir de `hooks/useServerClock.js` : la
 * seule horloge qui fasse foi est celle du serveur, celle qui déclenche
 * réellement les départs. Un poste en avance de deux minutes ouvrirait sinon la
 * journée suivante avant l'antenne.
 */
export function todayBroadcastDayKey(now: Date): string {
  return broadcastDayKey(now)
}

/**
 * Jour de la semaine d'une date civile d'antenne : 1 = lundi … 7 = dimanche.
 * Il se lit sur la date elle-même, pas sur le fuseau du navigateur : minuit à
 * Paris, c'est encore la veille à Londres, et la colonne du lundi se serait
 * affichée « DIM ».
 */
export function broadcastWeekday(value: string | Date): number {
  const [year, month, day] = civilDayOf(value)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay() || 7
}

/** Numéro de quantième d'une journée d'antenne — « 5 » pour le 5 août. */
export function broadcastDayNumber(value: string | Date): number {
  return civilDayOf(value)[2]
}

/**
 * Heure d'horloge d'antenne d'un instant, au format `HH:MM:SS`. C'est la valeur
 * à pré-remplir dans les champs de saisie : l'opérateur programme à l'heure de
 * la chaîne, pas à celle de son poste.
 */
export function broadcastTimeOfDay(instant: Date): string {
  const parts = antennaParts(instant.getTime())
  const pad = (part: number) => String(part).padStart(2, '0')
  return `${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`
}

/**
 * Instant réel d'une heure d'horloge d'antenne saisie par l'opérateur —
 * l'inverse de `broadcastTimeOfDay`. `clock` vaut `HH:MM` ou `HH:MM:SS`.
 *
 * Quand la saisie tombe dans l'heure escamotée du passage à l'heure d'été
 * (02:30 le dernier dimanche de mars), l'instant rendu est celui de la même
 * position dans la journée, soit 03:30 : cette minute-là n'existe pas à
 * l'antenne, autant la ramener sur celle qui sera réellement diffusée.
 */
export function instantAtBroadcastClock(dayKey: string, clock: string): Date {
  const [hour, minute, second] = clock.split(':').map(Number)
  const [year, month, day] = civilDayOf(dayKey)
  return instantOf(year, month, day, hour, minute, (second || 0) * 1000)
}

/** Libellé d'une journée d'antenne (« lundi 5 août »), lu sur la date civile. */
export function broadcastDayLabel(
  value: string | Date,
  options: Intl.DateTimeFormatOptions,
): string {
  const [year, month, day] = civilDayOf(value)
  return new Date(Date.UTC(year, month - 1, day))
    .toLocaleDateString('fr-FR', { ...options, timeZone: 'UTC' })
}

/** Minuit d'antenne du jour indiqué. */
export function startOfBroadcastDay(value: string | Date): Date {
  const [year, month, day] = civilDayOf(value)
  return instantOf(year, month, day)
}

/**
 * Minuit d'antenne `days` jours civils plus tard. Le décalage se compte en
 * jours, jamais en heures : une semaine vaut 167, 168 ou 169 heures.
 */
export function addBroadcastDays(value: string | Date, days: number): Date {
  const [year, month, day] = civilDayOf(value)
  const shifted = new Date(Date.UTC(year, month - 1, day + days))
  return instantOf(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate())
}

/** Borne haute exclusive de la journée : minuit d'antenne du lendemain. */
export function endOfBroadcastDay(value: string | Date): Date {
  return addBroadcastDays(value, 1)
}

/** Fenêtre demi-ouverte `[from, to[` d'une journée d'antenne. */
export function broadcastDayWindow(value: string | Date): { from: Date; to: Date } {
  return { from: startOfBroadcastDay(value), to: endOfBroadcastDay(value) }
}

/** Durée réelle de la journée : 24 h, sauf les dimanches de 23 h et 25 h. */
export function broadcastDayHours(value: string | Date): number {
  const { from, to } = broadcastDayWindow(value)
  return Math.round((to.getTime() - from.getTime()) / 3_600_000)
}

/**
 * Position d'un instant dans la journée affichée, en heures écoulées depuis
 * minuit d'antenne. C'est la grandeur qui pilote la géométrie de la timeline :
 * continue et strictement croissante, y compris au retour à l'heure d'hiver où
 * l'horloge, elle, repasse deux fois par 02:00.
 */
export function broadcastOffsetHours(instant: string | Date, dayKey: string): number {
  const at = typeof instant === 'string' ? new Date(instant) : instant
  return (at.getTime() - startOfBroadcastDay(dayKey).getTime()) / 3_600_000
}

/** Inverse de `broadcastOffsetHours` : l'instant à N heures de minuit. */
export function instantAtOffsetHours(dayKey: string, hours: number): Date {
  return new Date(startOfBroadcastDay(dayKey).getTime() + Math.round(hours * 3_600_000))
}

/**
 * Heure d'horloge lue à une position de la timeline, pour les graduations. Le
 * 25 octobre, deux positions différentes portent le libellé « 02:00 » : c'est
 * exact, cette heure-là est diffusée deux fois.
 */
export function broadcastClockLabel(dayKey: string, offsetHours: number): string {
  const parts = antennaParts(instantAtOffsetHours(dayKey, offsetHours).getTime())
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`
}
